import React, { useState } from 'react';
import { CheckCircle, Copy, Shield, AlertTriangle, Clock, ClipboardCheck } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';

/**
 * Компонент отображения результатов генерации Bcrypt-хеша
 */
const ResultsView = ({ result, validateResult }) => {
  const [copied, setCopied] = useState(false);

  // Если нет результатов, не отображаем компонент
  if (!result && !validateResult) return null;

  // Обработчик копирования хеша в буфер обмена
  const copyToClipboard = () => {
    navigator.clipboard.writeText(result.hash)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Ошибка при копировании хеша:', err);
        alert('Не удалось скопировать хеш в буфер обмена.');
      });
  };

  // Разбор хеша на компоненты для подсветки
  const parseHash = (hash) => {
    if (!hash || !hash.startsWith('$2')) {
      return { prefix: hash, salt: '', checksum: '' };
    }

    try {
      const parts = hash.split('$');
      if (parts.length < 4) {
        return { prefix: hash, salt: '', checksum: '' };
      }

      const prefix = `$${parts[1]}$${parts[2]}$`;
      const remainingPart = parts[3];
      
      // Первые 22 символа после префикса - это соль
      const salt = remainingPart.substring(0, 22);
      // Остальное - это контрольная сумма
      const checksum = remainingPart.substring(22);

      return { prefix, salt, checksum };
    } catch (error) {
      console.error('Ошибка при разборе хеша:', error);
      return { prefix: hash, salt: '', checksum: '' };
    }
  };

  // Рендеринг хеша с подсветкой компонентов
  const renderHash = (hash) => {
    const { prefix, salt, checksum } = parseHash(hash);
    
    if (!prefix && !salt && !checksum) {
      return <span>{hash}</span>;
    }

    return (
      <>
        <span className="hash-prefix">{prefix}</span>
        <span className="hash-salt">{salt}</span>
        <span className="hash-checksum">{checksum}</span>
      </>
    );
  };

  // Если это результат валидации, отображаем его
  if (validateResult) {
    return (
      <ToolResult title="Результат проверки хеша">
        <div className={`p-4 rounded-lg border mb-4 ${
          validateResult.isMatch 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start">
            {validateResult.isMatch ? (
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
            )}
            <div>
              <h3 className={`text-lg font-semibold ${
                validateResult.isMatch 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {validateResult.isMatch 
                  ? 'Пароль соответствует хешу!' 
                  : 'Пароль не соответствует хешу.'}
              </h3>
              <p className={`text-sm ${
                validateResult.isMatch 
                  ? 'text-green-700 dark:text-green-300' 
                  : 'text-red-700 dark:text-red-300'
              }`}>
                {validateResult.isMatch 
                  ? 'Проверка прошла успешно. Введённый пароль действительно соответствует данному хешу.'
                  : 'Проверка не прошла. Либо пароль неверный, либо хеш был создан из другого пароля.'}
              </p>
            </div>
          </div>
        </div>

        <ResultItem 
          label="Проверяемый текст" 
          value={validateResult.text} 
          color="blue" 
          icon={<KeyCircle className="w-4 h-4"/>}
        />
        
        <div className="space-y-1 mb-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Проверяемый хеш</span>
          <div className="hash-highlight">{renderHash(validateResult.hash)}</div>
        </div>
      </ToolResult>
    );
  }

  // Основной режим - отображение сгенерированного хеша
  return (
    <ToolResult title="Результат генерации Bcrypt-хеша">
      <div className="mb-5 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-1">
              Хеш успешно сгенерирован!
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Вы можете скопировать хеш и использовать его в своём приложении для хранения или проверки паролей.
            </p>
            {result.isGenerated && (
              <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                <strong>Важно:</strong> Сохраните сгенерированный пароль, поскольку восстановить его из хеша невозможно.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Хеш с подсветкой компонентов */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bcrypt хеш</span>
          <button
            type="button"
            onClick={copyToClipboard}
            className={`inline-flex items-center justify-center p-1.5 text-xs font-medium rounded-md
              ${copied 
                ? 'bg-green-100 text-green-700 dark:bg-green-800/30 dark:text-green-300' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              } transition-colors duration-200`}
            title="Копировать хеш"
          >
            {copied 
              ? <><ClipboardCheck className="w-4 h-4 mr-1.5" /> Скопировано</>
              : <><Copy className="w-4 h-4 mr-1.5" /> Копировать</>
            }
          </button>
        </div>
        <div className="hash-highlight">{renderHash(result.hash)}</div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1.5"></span>
            <span className="text-gray-600 dark:text-gray-400">Алгоритм/раунды</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1.5"></span>
            <span className="text-gray-600 dark:text-gray-400">Соль (22 символа)</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1.5"></span>
            <span className="text-gray-600 dark:text-gray-400">Хеш пароля</span>
          </div>
        </div>
      </div>

      {/* Детали хеша */}
      <div className="space-y-3">
        <ResultItem 
          label="Исходный текст" 
          value={result.plaintext} 
          color="blue" 
          icon={<KeyCircle className="w-4 h-4"/>}
        />

        <ResultItem 
          label="Количество раундов" 
          value={result.rounds} 
          color="orange" 
          icon={<Clock className="w-4 h-4"/>}
        />

        <ResultItem 
          label="Формат хеша" 
          value={
            result.format === 'bcrypt' ? 'Bcrypt ($2b$)' : 
            result.format === 'bcrypt_compat' ? 'Bcrypt совместимый ($2a$)' : 
            result.format === 'bcrypt_y' ? 'Bcrypt ($2y$)' :
            result.format
          } 
        />

        <ResultItem 
          label="Дата и время создания" 
          value={new Date(result.timestamp).toLocaleString('ru-RU')} 
        />

        {result.isGenerated && (
          <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-1" />
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Этот пароль был автоматически сгенерирован системой. Сохраните его в надежном месте!
              </p>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

// Маленький фикс - компонент иконки KeyCircle
const KeyCircle = ({ className }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M10 9.5a2.5 2.5 0 1 1 5 0a2.5 2.5 0 0 1-5 0z" />
      <path d="M12.5 12v3.5" />
      <path d="M11 14h3" />
    </svg>
  );
};

export default ResultsView;